import { searchProductDetails } from 'api/services/ProjectsService';
import { EventProduct, Product, ProductType } from 'types/Products';
import { transformSubcategoriesIntoCategories } from '../projects/Sections/ProductsTabs/helpers/helpers';
import { ProductDetailsBySearchApi } from 'api/services/ProductsService';
import { useCallback, useEffect, useState } from 'react';
import { getProductBookmark } from 'api/services/ProductServices';
import { useAuth } from 'react-oidc-context';
import { captureException } from '@sentry/react';

export const useProductPageModel = () => {
  const getProduct = useCallback(
    (
      eventKey: string | undefined,
      productKey: string,
      setProduct: (product: EventProduct & Product) => void
    ) => {
      !eventKey
        ? searchProductDetails(
            productKey,
            (res: any) => setProduct(remapProduct(res.data)),
            // window.location.assign is used here on purpose.
            // We need a "real" navigation here to give
            // google the status code 404. It's very important
            // for the crawler
            () => window.location.assign(`/notFound`)
          )
        : ProductDetailsBySearchApi(
            productKey,
            eventKey,
            (res: any) => {
              setProduct(remapProduct(res.data));
            },
            () => {
              // This should never happen, therefore we log it now to understand it better.
              // Seems like this path is broken anyways, throwing a 'Cannot read properties of undefined'
              captureException(
                new Error(
                  `UXB Product ${productKey} not found, falling back to legacy API`
                )
              );
              //if the product doesnt exist in the event we try to load the normal product page
              searchProductDetails(
                productKey,
                (res: any) => setProduct(remapProduct(res.data)),
                () => window.location.assign(`/notFound`)
              );
            }
          );
    },
    []
  );

  return { getProduct };
};

export const useProductBookmarkModel = (product?: EventProduct & Product) => {
  const [bookmarked, setBookmarked] = useState<boolean>(false);

  const { isAuthenticated } = useAuth();
  useEffect(() => {
    // @ts-ignore
    product && isAuthenticated && isProductBookmarked(product, setBookmarked);
  }, [isAuthenticated, product]);

  return {
    isProductBookmarked,
    bookmarked,
    setBookmarked
  };
};

const isProductBookmarked = (
  product: EventProduct,
  setBookmarked: React.Dispatch<React.SetStateAction<boolean>>
) => {
  getProductBookmark(
    product,
    (res: any) => setBookmarked(res.data),
    () => {
      setBookmarked(false);
    }
  );
};

const remapProduct = (product: Product & EventProduct) => {
  interface technicalWithSubcategories {
    claims: string;
    organoleptic: string;
    properties: string;
  }
  if (product?.categories?.technical) {
    const technicalWithSubcategories: (keyof technicalWithSubcategories)[] = [
      'claims',
      'organoleptic',
      'properties'
    ];
    let subcategoriesResults = {};
    technicalWithSubcategories.forEach((c: string) => {
      const cat = c as keyof technicalWithSubcategories;
      if (
        product?.categories?.technical &&
        product?.categories?.technical[cat]
      ) {
        const transformed = transformSubcategoriesIntoCategories(
          product.categories.technical[cat]
        );

        subcategoriesResults = {
          ...subcategoriesResults,
          ...transformed
        };
      }
    });

    Object.keys(subcategoriesResults).forEach(each => {
      // @ts-ignore
      const arr = subcategoriesResults[each];
      // @ts-ignore
      product.categories.technical[each] = arr;
    });
  }

  return product;
};

//This fixes it now for formulations, in ingredients and services we didnt notice any such issue up to date, but perhaps in future this can be
//extended to cover all the types
export const shouldShowFormulationSection = (product: Partial<Product>) => {
  if (!(product.type === ProductType.FORMULATIONS)) return true;
  else
    return (
      product?.categories?.technical?.applications ||
      product?.categories?.['use-storage']?.howtouse ||
      product?.categories?.['use-storage']?.['storage-requirements'] ||
      product.categories?.['formulation-procedure']?.formulation ||
      (product.categories?.['formulation-procedure'] &&
        product.categories['formulation-procedure'].procedure?.length > 0) ||
      product.assets
    );
};
